import { useRef, type KeyboardEvent, type ReactNode } from "react";
import { cx } from "../cx";

export interface ChipProps {
  children: ReactNode;
  selected?: boolean;
  onClick?: () => void;
  /** `float`: a white chip with a shadow over a map (a legend entry; not a button unless onClick is given). */
  look?: "pill" | "float";
  className?: string;
}

/** A pill chip. A toggle (`aria-pressed`) when it has onClick, otherwise a static label. */
export function Chip({ children, selected, onClick, look = "pill", className }: ChipProps) {
  const cls = cx("lcc-chip", look === "float" && "lcc-chip--float", className);
  if (!onClick) return <span className={cls}>{children}</span>;
  return (
    <button type="button" className={cls} aria-pressed={!!selected} onClick={onClick}>
      {children}
    </button>
  );
}

export interface FilterOption<V extends string> {
  value: V;
  label: string;
  count?: number;
}

export interface FilterChipsProps<V extends string> {
  /** Accessible name of the group, e.g. "Show". */
  label: string;
  options: FilterOption<V>[];
  value: V;
  onChange: (value: V) => void;
}

/** One-of-many filter as pill toggles ("All 38", "No operator 1"...). */
export function FilterChips<V extends string>({ label, options, value, onChange }: FilterChipsProps<V>) {
  return (
    <div className="lcc-chips" role="group" aria-label={label}>
      {options.map((o) => (
        <Chip key={o.value} selected={o.value === value} onClick={() => onChange(o.value)}>
          {o.label}
          {o.count !== undefined && <span>{o.count}</span>}
        </Chip>
      ))}
    </div>
  );
}

export interface TabItem<V extends string> {
  value: V;
  label: string;
  count?: number;
  /** Red label (e.g. "Past deadline"); the words must say it too. */
  tone?: "danger";
}

export interface TabsProps<V extends string> {
  label: string;
  tabs: TabItem<V>[];
  value: V;
  onChange: (value: V) => void;
  /** Id prefix: tab `${idBase}-tab-${value}` controls panel `${idBase}-panel-${value}`. */
  idBase: string;
}

/** Pill tabs (role tablist): arrow keys, Home and End move between tabs; only the selected tab is in the Tab order. */
export function Tabs<V extends string>({ label, tabs, value, onChange, idBase }: TabsProps<V>) {
  const refs = useRef<Array<HTMLButtonElement | null>>([]);
  function onKey(e: KeyboardEvent, i: number) {
    let next = -1;
    if (e.key === "ArrowRight") next = (i + 1) % tabs.length;
    else if (e.key === "ArrowLeft") next = (i - 1 + tabs.length) % tabs.length;
    else if (e.key === "Home") next = 0;
    else if (e.key === "End") next = tabs.length - 1;
    if (next < 0) return;
    e.preventDefault();
    onChange(tabs[next].value);
    refs.current[next]?.focus();
  }
  return (
    <div role="tablist" aria-label={label} className="lcc-tabs">
      {tabs.map((t, i) => {
        const selected = t.value === value;
        return (
          <button
            key={t.value}
            ref={(el) => {
              refs.current[i] = el;
            }}
            type="button"
            role="tab"
            id={`${idBase}-tab-${t.value}`}
            aria-selected={selected}
            aria-controls={`${idBase}-panel-${t.value}`}
            tabIndex={selected ? 0 : -1}
            className={cx("lcc-tab", t.tone === "danger" && "lcc-tab--danger")}
            onClick={() => onChange(t.value)}
            onKeyDown={(e) => onKey(e, i)}
          >
            {t.label}
            {t.count !== undefined && ` · ${t.count}`}
          </button>
        );
      })}
    </div>
  );
}
